import { cn } from '../../lib/utils.ts';
import logoGreen from '../../assets/pyetro_logo_green.svg';
import logoLight from '../../assets/pyetro_logo.svg';
import { logoBox, logoVariants, type LogoVariantProps } from './logo-variants';

/**
 * The pyetro logo lockup — marchio + logotype, never one without the other
 * (manual §10 Don'ts lists "logotipo senza marchio" explicitly; §18.3 allows the
 * marchio alone, which is a separate asset and has no call site yet).
 *
 * Geometry, including §18.5's clear space, comes from `logo-variants.ts` so this
 * component and the website's `Logo.astro` cannot drift apart. Passing `height`
 * is the only sizing knob: width follows the lockup's aspect ratio and the clear
 * space follows the height, so there is no way to size the logo and forget the
 * padding — which is what every hand-rolled `<img className="h-8 w-auto">` did.
 */
type LogoProps = {
  /** Rendered height in px. Width and clear space are derived from it. */
  height?: number;
  /** Wrap in an anchor. Omit for a decorative/among-other-content logo. */
  href?: string;
  /** Accessible name. The logo is the product name, so this is the default. */
  alt?: string;
  className?: string;
} & Pick<LogoVariantProps, 'colourway'>;

export const Logo = ({
  height = 32,
  href,
  alt = 'pyetro',
  colourway = 'light',
  className,
}: LogoProps) => {
  const { width, clearSpace } = logoBox('lockup', height);
  const src = colourway === 'green' ? logoGreen : logoLight;

  const image = (
    <img
      src={src}
      width={width}
      height={height}
      alt={alt}
      style={{ padding: clearSpace }}
    />
  );

  if (href) {
    return (
      <a
        href={href}
        className={cn(
          logoVariants({ colourway, interactive: true }),
          className,
        )}
      >
        {image}
      </a>
    );
  }

  return (
    <span className={cn(logoVariants({ colourway }), className)}>{image}</span>
  );
};
